import type { User, AuthState } from '@/types/auth';


const setCookie = (name: string, value: string, days: number = 7) => {
  if (typeof window === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};


const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null;
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length, cookie.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  if (typeof window === 'undefined') return;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`;
};

/** SessionStorage key for impersonation token (per-tab; used so admin + customer can be open in different tabs). */
export const IMPERSONATION_TOKEN_KEY = 'impersonationToken';

export const authUtils = {
  getAccessToken(): string | null {
    return getCookie('accessToken');
  },

  setAccessToken(token: string, days?: number): void {
    setCookie('accessToken', token, days ?? 7);
  },

  removeAccessToken(): void {
    deleteCookie('accessToken');
  },

  getUser(): User | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  setUser(user: User): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('user', JSON.stringify(user));
  },

  removeUser(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('user');
  },

  getBusinessUnit(): string | null {
    return getCookie('selectedBusinessUnit');
  },

  setBusinessUnit(businessUnit: string, days?: number): void {
    setCookie('selectedBusinessUnit', businessUnit, days ?? 7);
  },

  removeBusinessUnit(): void {
    deleteCookie('selectedBusinessUnit');
  },

  clearAll(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('user');
    deleteCookie('accessToken');
    deleteCookie('selectedBusinessUnit');
  },

  getImpersonationToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  },

  setImpersonationToken(token: string): void {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
  },

  clearImpersonationToken(): void {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  },

  isImpersonating(): boolean {
    return !!this.getImpersonationToken();
  },

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getUser();
    return !!(token && user);
  },

  getInitialAuthState(): AuthState {
    return {
      user: this.getUser(),
      accessToken: this.getAccessToken(),
      isAuthenticated: this.isAuthenticated(),
      isLoading: false,
      businessUnit: this.getBusinessUnit(),
    };
  },
};

